import { API_BASE_URL } from '../config';
import { Contact, ContactWithDetails, Opportunity } from '../types';

export interface ContactListResponse {
  items: Contact[];
  total: number;
  page: number;
  size: number;
  totalPages: number;
}

export interface FetchContactsOptions {
  page?: number;
  size?: number;
  search?: string;
  filter?: string;
}

export async function fetchContacts(
  accessToken: string,
  options?: FetchContactsOptions
): Promise<ContactListResponse> {
  const params = new URLSearchParams();

  if (options?.page !== undefined) {
    params.append('page', options.page.toString());
  }

  if (options?.size !== undefined) {
    params.append('size', options.size.toString());
  } else {
    params.append('size', '50');
  }

  if (options?.search) {
    params.append('search', options.search);
  }

  if (options?.filter && options.filter !== 'all') {
    params.append('filter', options.filter);
  }

  const queryString = params.toString();
  const endpoint = `${API_BASE_URL}/backoffice/contacts${queryString ? '?' + queryString : ''}`;

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar contatos');
  }

  const payload = await response.json();
  // API wraps response in { success, data, error } via ResponseWrapperFilter
  const innerData = payload.data || {};
  return {
    items: innerData.items || innerData.data || [],
    total: innerData.total || innerData.totalItems || 0,
    page: innerData.page || 0,
    size: innerData.size || 50,
    totalPages: innerData.totalPages || 0
  };
}

export async function fetchContactById(
  contactId: string,
  accessToken: string
): Promise<ContactWithDetails> {
  const response = await fetch(`${API_BASE_URL}/backoffice/contacts/${contactId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar detalhes do contato');
  }

  const payload = await response.json();
  // API wraps response in { success, data, error }
  return payload.data || payload;
}

export async function fetchContactByPhone(
  phone: string,
  accessToken: string
): Promise<ContactWithDetails> {
  const response = await fetch(`${API_BASE_URL}/backoffice/contacts/by-phone/${encodeURIComponent(phone)}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar contato por telefone');
  }

  const payload = await response.json();
  return payload.data || payload;
}

export async function fetchContactOpportunities(
  contactId: string,
  accessToken: string
): Promise<Opportunity[]> {
  const response = await fetch(`${API_BASE_URL}/backoffice/contacts/${contactId}/opportunities`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar oportunidades do contato');
  }

  const payload = await response.json();
  // API wraps response in { success, data, error }
  return payload.data || payload || [];
}
